import React, { useState } from 'react';
import {
  Box, Stack, TextField, Button, MenuItem, Typography, Paper,
  FormControl, InputLabel, Select, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate } from 'react-router-dom';

import { addIncident } from './incidentsService';
import { useI18n } from '../../i18n/i18n';
import CameraCapture from '../../components/CameraCapture';
import SafeImage from '../../components/SafeImage';

import { CLIENTS as clientsRaw } from '../../data/clients';

function normalizeClients(list) {
  if (!Array.isArray(list)) return [];
  return list.map((x) => {
    if (typeof x === 'string') return { id: x, name: x };
    if (x && typeof x === 'object') {
      return {
        id: x.id ?? x.name ?? x.title ?? String(Math.random()),
        name: x.name ?? x.title ?? String(x?.toString?.() || ''),
        ...x,
      };
    }
    return { id: String(x), name: String(x) };
  }).filter(x => x.name && x.name.trim().length > 0);
}

const CLIENTS = normalizeClients(clientsRaw);

export default function IncidentsCreatePage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    responsible: '',
    clientId: '',
    type: 'client',
    description: '',
    photos: []
  });
  const [isCameraOpen, setCameraOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePhotoCaptured = (dataUrl) => {
    if (dataUrl) {
      const newPhoto = {
        id: `photo_${Date.now()}`,
        dataUrl,
        name: `incident_${Date.now()}.jpg`,
        timestamp: new Date().toISOString()
      };
      setForm(prev => ({ ...prev, photos: [...prev.photos, newPhoto] }));
    }
    setCameraOpen(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newPhoto = {
          id: `file_${Date.now()}`,
          dataUrl: e.target.result,
          name: file.name,
          timestamp: new Date().toISOString()
        };
        setForm(prev => ({ ...prev, photos: [...prev.photos, newPhoto] }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (photoId) => {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    
    if (!form.date || !form.responsible || !form.description || !form.type) {
      alert(t('incidents_form_incomplete') || 'Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const selectedClient = CLIENTS.find(c => c.id === form.clientId);
      
      const incidentData = {
        date: form.date,
        type: form.type,
        clientId: form.clientId,
        clientName: selectedClient?.name || '',
        description: form.description,
        responsible: form.responsible,
        photos: form.photos,
        status: 'open'
      };

      await addIncident(incidentData);
      
      alert(t('incidents_created_ok') || 'Incident created successfully');
      navigate('/incidents/list');
      
    } catch (err) {
      console.error('Error creating incident:', err);
      alert(t('incidents_created_fail') || 'Failed to create incident');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (isCameraOpen) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('incidents_take_photo') || 'Take Photo'}
        </Typography>
        <CameraCapture onShot={handlePhotoCaptured} />
        <Button 
          variant="outlined" 
          onClick={() => setCameraOpen(false)}
          sx={{ mt: 2 }}
        >
          {t('common_cancel') || 'Cancel'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('incidents_create_title') || 'Create New Incident'}
      </Typography>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <TextField
            label={t('common_date') || 'Date'}
            type="date"
            value={form.date}
            onChange={e => updateField('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          <TextField
            label={t('incidents_responsible') || 'Reported By'}
            value={form.responsible}
            onChange={e => updateField('responsible', e.target.value)}
            placeholder={t('incidents_responsible_placeholder') || 'Enter your name'}
            fullWidth
            required
          />

          <Autocomplete
            options={CLIENTS}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt?.name || ''))}
            isOptionEqualToValue={(opt, val) => (opt?.id ?? opt?.name) === (val?.id ?? val?.name)}
            value={CLIENTS.find(o => o.id === form.clientId) || null}
            onChange={(_evt, option) => {
              updateField('clientId', option?.id || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('incidents_client') || 'Client'}
                placeholder={t('incidents_client_placeholder') || 'Select client...'}
                fullWidth
              />
            )}
            clearOnBlur={false}
            autoHighlight
            includeInputInList
            blurOnSelect
          />

          <FormControl fullWidth required>
            <InputLabel>{t('incidents_type') || 'Incident Type'}</InputLabel>
            <Select
              value={form.type}
              label={t('incidents_type') || 'Incident Type'}
              onChange={e => updateField('type', e.target.value)}
            >
              <MenuItem value="client">{t('incidents_type_client') || 'Client'}</MenuItem>
              <MenuItem value="technical">{t('incidents_type_technical') || 'Technical'}</MenuItem>
              <MenuItem value="logistics">{t('incidents_type_logistics') || 'Logistics'}</MenuItem>
              <MenuItem value="safety">{t('incidents_type_safety') || 'Safety'}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={t('incidents_description') || 'Description'}
            value={form.description}
            onChange={e => updateField('description', e.target.value)}
            multiline
            rows={4}
            placeholder={t('incidents_description_placeholder') || 'Describe the incident in detail...'}
            fullWidth
            required
          />

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {t('incidents_photos') || 'Photos'}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<AddIcon />}
                onClick={() => setCameraOpen(true)}
              >
                {t('incidents_take_photo') || 'Take Photo'}
              </Button>
              <Button 
                variant="outlined" 
                component="label"
              >
                {t('incidents_upload_photo') || 'Upload Photo'}
                <input 
                  type="file" 
                  accept="image/*" 
                  hidden 
                  onChange={handleFileUpload} 
                />
              </Button>
            </Stack>

            {form.photos.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {form.photos.map((photo) => (
                  <Box key={photo.id} sx={{ position: 'relative' }}>
                    <SafeImage 
                      src={photo.dataUrl} 
                      alt={photo.name}
                      style={{ width: 100, height: 100, borderRadius: 1 }}
                    />
                    <Chip
                      label={photo.name}
                      onDelete={() => removePhoto(photo.id)}
                      size="small"
                      sx={{ position: 'absolute', top: -10, right: -10 }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={() => navigate('/incidents/list')}
              disabled={loading}
            >
              {t('common_cancel') || 'Cancel'}
            </Button>
            <Button 
              type="submit"
              variant="contained" 
              startIcon={<AddIcon />}
              disabled={loading}
            >
              {loading 
                ? (t('common_saving') || 'Saving...') 
                : (t('incidents_create_btn') || 'Create Incident')
              }
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}